import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Card,
  Stack,
  Button,
  Drawer,
  Box,
  Chip,
  Typography,
  ButtonGroup,
  Popper,
  MenuItem,
  MenuList,
  ClickAwayListener,
  Paper,
  Grow,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import JsFileDownloader from 'js-file-download';
import {
  DEFAULT_LIST_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/content-videos.config';
import Iconify from '../common/iconify.component';
import {
  downloadExportVideosData,
  exportVideosData,
  getListData,
  listExportVideoData,
  reprocessVideoAsset,
} from '../../services/videos.service';
import { ACTIONS, SCHEDULE_EXPORT_STATUS } from '../../config/const.config';
import SnackbarInfo from '../common/snackbar-info.component';
import replaceIdUrl from '../../utils/url.util';
import ROUTE_PATH from '../../config/routes.config';
import CreateContentVideo from './create-content-video.component';
import FilterVideos from '../filter-videos/filter-video.component';
import Workspace from '../form-elements/workspace.component';
import { FiltersContext } from '../../context/filter.context';
import prepareDynamicColumns from '../../utils/dynamic-columns.util';
import ListVideosData from '../table-elements/list-videos-data.component';
import EditVideosList from './edit-videos-list.component';
import useAccess from '../../hooks/access.hook';
import useFilteredActions from '../../hooks/filtered-action.hook';
import ManageUserEdits from '../video-edit-approvals/index.component';
import AssignChannelsVideo from './assign-channels-video.component';
import {
  getLocalFilterValue,
  setLocalFilterValue,
} from '../../utils/local-cache.util';
import useAbortableRequest from '../../hooks/abortable-request.hook';
import FieldChooser from '../form-elements/field-chooser.component';
import {
  formatTimestamp,
  getCurrentTimestamp,
} from '../../utils/datetime.util';
import DistributorFilterVideos from '../filter-videos/distributor-filter-videos.component';
import ConfirmPopup from '../common/confirm-popup.component';

const ListContentVideos = () => {
  const FILTERS_KEY = 'video-filters';
  const actions = useFilteredActions('video_library', LISTING_ACTIONS);
  const { getSignal, isAbortError } = useAbortableRequest();
  const navigate = useNavigate();
  const { hasOperationAccess, hasContentOwnerAccess, hasAdminAccess } =
    useAccess();
  const { openFilter, handleToggleFilters } = useContext(FiltersContext);
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    columns: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: {
      licenese_expired_videos: {
        apply: true,
        condition: 'EQUALS_TO',
        title: 'Show License Expired Videos',
        type: 'checkbox',
        value: false,
      },
    },
    filtersCounter: 0,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [enabledEditMode, setEnabledEditMode] = useState(false);
  const [columnFields, setColumnFields] = useState([]);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [downloadList, setDownloadList] = useState([]);
  const anchorRef = useRef(null);

  const handleExportData = () => {
    setProcessing(true);
    setSnackbarInfo({ ...snackbarInfo, show: false });

    const localFilters = getLocalFilterValue(FILTERS_KEY);
    const appliedFilters =
      Object.keys(localFilters).length > 0
        ? localFilters
        : options.appliedFilters;
    const filters = Object.entries(appliedFilters)
      .filter(([, filter]) => filter.apply)
      .map(([field, filter]) => ({
        field,
        ...filter,
      }));

    const payload = {
      filters,
      downloadByCronJob: options.totalRows > 500,
    };
    exportVideosData(payload)
      .then((res) => {
        if (options.totalRows > 500) {
          setDownloadList((prev) => [res.data, ...prev]);
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: 'Export pushed in queue.',
          });
        } else {
          // eslint-disable-next-line no-unused-vars
          const download = new JsFileDownloader(
            res,
            `videos-data-${getCurrentTimestamp()}.csv`
          );
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: 'Data exported successfully.',
          });
        }
        setProcessing(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setProcessing(false);
      });
  };

  const handleDownloadExportData = (data) => {
    downloadExportVideosData(data.id)
      .then((res) => {
        // eslint-disable-next-line no-unused-vars
        const download = new JsFileDownloader(res, data.downloadPath);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
      });
  };

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleApplyFilters = (selectedFilters) => {
    setLocalFilterValue(FILTERS_KEY, selectedFilters);
    const counter =
      Object.values(selectedFilters).filter((filter) => filter.apply).length ||
      0;

    setOptions({
      ...options,
      appliedFilters: { ...selectedFilters },
      filtersCounter: counter,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleClearFilters = () => {
    setLocalFilterValue(FILTERS_KEY, {});
    setOptions({
      ...options,
      appliedFilters: {
        licenese_expired_videos: {
          apply: true,
          condition: 'EQUALS_TO',
          title: 'Show License Expired Videos',
          type: 'checkbox',
          value: false,
        },
      },
      filtersCounter: 0,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setSnackbarInfo({ ...snackbarInfo, show: false });

    if (selectedAction === ACTIONS.EDIT.value) {
      const url = replaceIdUrl(ROUTE_PATH.VIDEO_EDIT, selectedData.id);
      navigate(url);
    } else if (selectedAction === ACTIONS.VIEW.value) {
      const url = replaceIdUrl(ROUTE_PATH.VIDEO_VIEW_DETAILS, selectedData.id);
      navigate(url);
    } else if (selectedAction === ACTIONS.PLAY_VIDEO.value) {
      const url = replaceIdUrl(ROUTE_PATH.VIDEO_PREVIEW, selectedData.id);
      const popup = window.open(url, '_blank', 'width=720,height=405');
      if (!popup) {
        setTimeout(() => {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'warning',
            message: 'Popup blocked! Please allow popups for this site.',
          });
        }, 50);
      }
    }
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleDeleteFilter = (field) => {
    if (field === 'clear-all') {
      handleClearFilters();
      return;
    }

    const updatedFilters = { ...options.appliedFilters };
    if (field === 'licenese_expired_videos') {
      updatedFilters[field] = { ...updatedFilters[field], value: false };
    } else {
      delete updatedFilters[field];
    }
    setLocalFilterValue(FILTERS_KEY, updatedFilters);

    setOptions({
      ...options,
      appliedFilters: updatedFilters,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const onFieldClick = (fields) => {
    setColumnFields(fields);
    handleRefreshData();
  };

  const handleReprocessAssetData = (id, isTrailer) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    reprocessVideoAsset(id, isTrailer)
      .then(() => {
        handleActionSuccess(
          `${
            isTrailer ? 'Trailer' : 'Video'
          } processed successfully and queued for encoding.`
        );
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  useEffect(() => {
    const signal = getSignal();

    setOptions({ ...options, loading: true });
    const localFilters = getLocalFilterValue(FILTERS_KEY);

    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    if (selectedWorkspace?.id) {
      params.push(`workspaceId=${selectedWorkspace.id}`);
    }

    const appliedFilters =
      Object.keys(localFilters).length > 0
        ? localFilters
        : options.appliedFilters;
    const filters = Object.entries(appliedFilters)
      .filter(([, filter]) => filter.apply)
      .map(([field, filter]) => ({
        field,
        ...filter,
      }));
    if (filters.search) {
      params.push(`q=${encodeURIComponent(filters.search)}`);
    }

    const uri = '';
    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getListData(
      uri + paramsQuery,
      {
        filter: filters,
        columnFields,
      },
      signal
    )
      .then((res) => {
        if (columnFields.length === 0) {
          const defaultFields = res?.data?.columnsFields
            .filter((item) => item.id)
            .map((item) => item.fieldName);
          setColumnFields(defaultFields);
        }

        let columns = [];
        if (selectedWorkspace?.id) {
          columns = prepareDynamicColumns(res?.data?.columnsFields);
        } else if (columnFields?.length > 0) {
          columns = [
            ...DEFAULT_LIST_COLUMNS,
            ...prepareDynamicColumns(res?.data?.columnsFields).filter(
              (col) =>
                col.dataKey !== 'title' &&
                col.dataKey !== 'encodeStatus' &&
                col.dataKey !== 'trailerEncodeStatus'
            ),
          ];
        } else {
          columns = DEFAULT_LIST_COLUMNS;
        }
        if (!signal.aborted) {
          setOptions({
            ...options,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            columns,
            appliedFilters: appliedFilters || {},
            error: false,
          });
        }
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter, selectedWorkspace?.id]);

  useEffect(() => {
    listExportVideoData()
      .then((res) => {
        setDownloadList(res.data);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
      });
  }, [options.reloadCounter]);

  return (
    <Box
      sx={{
        transition: 'margin 0.3s ease, width 0.3s ease',
        width: openFilter ? 'calc(100% - 300px)' : '100%',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 0, py: 2 }}
      >
        <Box width="60%" display="flex" alignItems="center">
          {hasOperationAccess('video_library', 'create') && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<Iconify icon="ic:round-add" />}
              onClick={() => handleActionSelect(ACTIONS.CREATE.value, null)}
            >
              Create New
            </Button>
          )}
          {hasAdminAccess() && (
            <>
              <Workspace
                id="workspace"
                name="workspace"
                label="Workspace"
                defaultValue={selectedWorkspace}
                onChange={(v) => {
                  setSelectedWorkspace(v);
                  setColumnFields([]);
                }}
                sx={{ width: '30%', mx: 2 }}
              />
              <Box width="40%">
                <FieldChooser
                  id="field_chooser"
                  name="field_chooser"
                  label="Field Chooser"
                  defaultValue={columnFields}
                  sx={{ width: '100%' }}
                  onFieldClick={onFieldClick}
                />
              </Box>
            </>
          )}
        </Box>
        <Box
          width="40%"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
        >
          {hasOperationAccess('video_library', 'edit') && (
            <Button
              color="inherit"
              variant="contained"
              startIcon={<Iconify icon="bx:edit" />}
              onClick={() => setEnabledEditMode(true)}
              disabled={options.loading}
              sx={{ mr: 1 }}
            >
              Edit Mode
            </Button>
          )}
          <Button
            color="inherit"
            variant="contained"
            onClick={handleRefreshData}
            disabled={options.loading}
            sx={{ mr: 1, fontSize: '1.25rem' }}
          >
            <Iconify icon="ic:twotone-refresh" />
          </Button>

          {hasAdminAccess() && (
            <Box>
              <ButtonGroup
                variant="contained"
                ref={anchorRef}
                aria-label="export-videos"
                disabled={processing}
              >
                <Button
                  onClick={() => setShowExportOptions(!showExportOptions)}
                  sx={{ fontSize: '1.25rem' }}
                >
                  <Iconify icon="ic:baseline-download" />
                </Button>
              </ButtonGroup>
              <Popper
                open={showExportOptions}
                anchorEl={anchorRef.current}
                transition
                disablePortal
                sx={{ zIndex: 99999 }}
              >
                {({ TransitionProps }) => (
                  <Grow {...TransitionProps}>
                    <Paper elevation={3}>
                      <ClickAwayListener
                        onClickAway={() => setShowExportOptions(false)}
                      >
                        <MenuList id="export-videos-button-menu" autoFocusItem>
                          <MenuItem
                            disabled={
                              options.loading ||
                              processing ||
                              options.totalRows === 0
                            }
                            onClick={() => handleExportData()}
                          >
                            <Typography variant="body2">
                              {processing && (
                                <Iconify
                                  icon="line-md:loading-loop"
                                  sx={{ mr: 1 }}
                                />
                              )}
                              {options.totalRows > 500
                                ? 'Push to Download Queue'
                                : 'Export Data'}
                            </Typography>
                          </MenuItem>

                          {downloadList.map((item, idx) => (
                            <>
                              {idx === 0 && <Divider />}

                              <MenuItem
                                disabled={
                                  options.loading ||
                                  processing ||
                                  item.status ===
                                    SCHEDULE_EXPORT_STATUS.IN_PROGRESS ||
                                  item.status === SCHEDULE_EXPORT_STATUS.PENDING
                                }
                                onClick={() => handleDownloadExportData(item)}
                              >
                                <Typography variant="body2">
                                  {(item.status ===
                                    SCHEDULE_EXPORT_STATUS.IN_PROGRESS ||
                                    item.status ===
                                      SCHEDULE_EXPORT_STATUS.PENDING) && (
                                    <Iconify
                                      icon="line-md:loading-loop"
                                      sx={{ mr: 1 }}
                                    />
                                  )}
                                  {item.status ===
                                    SCHEDULE_EXPORT_STATUS.IN_PROGRESS ||
                                  item.status === SCHEDULE_EXPORT_STATUS.PENDING
                                    ? 'Preparing'
                                    : 'Saved'}{' '}
                                  {formatTimestamp(
                                    item.downloadFileName,
                                    'MM/DD/YYYY hh:mm A'
                                  )}
                                </Typography>
                              </MenuItem>
                            </>
                          ))}
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Box>
          )}
        </Box>
      </Stack>

      {options.appliedFilters &&
        Object.values(options.appliedFilters).some(
          (filter) =>
            filter.apply &&
            ((filter.type === 'checkbox' && filter.value) ||
              filter.type !== 'checkbox')
        ) && (
          <Box mb={2}>
            <Typography variant="body1" fontWeight="bold" display="inline">
              Filters Applied:{' '}
              <Chip
                key="all"
                label="Clear all"
                onDelete={() => handleDeleteFilter('clear-all')}
                sx={{ ml: 1, color: 'red' }}
              />
            </Typography>

            {Object.entries(options.appliedFilters)
              .filter(
                ([, filter]) =>
                  filter.apply &&
                  ((filter.type === 'checkbox' && filter.value) ||
                    filter.type !== 'checkbox')
              )
              .map(([field, filter]) => (
                <Chip
                  key={field}
                  label={filter.title || 'Active'}
                  onDelete={() => handleDeleteFilter(field)}
                  sx={{ ml: 1 }}
                />
              ))}
          </Box>
        )}

      <Card>
        <ListVideosData
          columns={options.columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
          showNumberedList
        />
      </Card>

      <Drawer
        anchor="right"
        open={openFilter}
        variant="persistent"
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: { width: 300, border: 'none', overflow: 'hidden' },
        }}
      >
        {hasContentOwnerAccess() ? (
          <DistributorFilterVideos
            values={options.appliedFilters}
            onCancel={handleToggleFilters}
            onSuccess={handleApplyFilters}
            onClear={handleClearFilters}
            from="video-library"
          />
        ) : (
          <FilterVideos
            values={options.appliedFilters}
            onCancel={handleToggleFilters}
            onSuccess={handleApplyFilters}
            onClear={handleClearFilters}
            from="video-library"
          />
        )}
      </Drawer>

      {doAction.action === ACTIONS.CREATE.value && (
        <CreateContentVideo
          title="Create New Video"
          copyChannelId={0}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}

      {doAction.action === ACTIONS.ASSIGN_CHANNELS.value && (
        <AssignChannelsVideo
          title={`Assign Channels to Video - ${doAction.data.title}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}

      {enabledEditMode && (
        <EditVideosList
          title="Content Management / Videos Library - Edit Mode"
          onCancel={() => setEnabledEditMode(false)}
          viewModeWorkspace={selectedWorkspace}
          videoListFilters={options.appliedFilters}
          initialColumnFields={columnFields}
          isEditMode
        />
      )}

      {doAction.action === ACTIONS.MANAGE_EDITS.value && (
        <ManageUserEdits
          title={`Manage Edits For Video - ${doAction.data.title}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
          byVideo
        />
      )}

      {doAction.action === ACTIONS.REPROCESS_VIDEO.value && (
        <ConfirmPopup
          title="Reprocess Video"
          message={`Do you want to reprocess ${doAction.data.title} video? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={() => handleReprocessAssetData(doAction.data.id, 0)}
        />
      )}

      {doAction.action === ACTIONS.REPROCESS_TRAILER.value && (
        <ConfirmPopup
          title="Reprocess Trailer"
          message={`Do you want to reprocess ${doAction.data.title} trailer? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={() => handleReprocessAssetData(doAction.data.id, 1)}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

export default ListContentVideos;
